import * as Styles from '../../../styles/ContentTableRow';
import SvgChevronDown from '../../../../../components/icons/ChevronDown';
import React from 'react';
import { NexoyaFunnelStepV2, NexoyaPortfolioParentContentsSortField } from '../../../../../types';
import styled from 'styled-components';
import { nexyColors } from '../../../../../theme';
import { BigHeaderCell } from 'routes/portfolio/styles/OptimizationProposal';
import { HeaderSortCell } from './HeaderSortCell';
import Checkbox from '../../../../../components/Checkbox';
import { contentLabelColumnId, labelColumnLabel } from '../../../../../utils/contentLabelColumns';
import type { LabelCategory } from '../../../../settings/labels/types';

export const HeaderCell = styled.div`
  color: ${nexyColors.neutral400};

  /* Headings/H6 */
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 15.4px */
  letter-spacing: 0.44px;
  text-transform: uppercase;
  padding: 0;
`;

/**
 * A Content Labels v2 category, as a column needs it: the id it is keyed by and
 * the name it is captioned with. A whole `LabelCategory` is assignable to this,
 * so the tab hands its categories straight through.
 */
export type ContentLabelColumnCategory = Pick<LabelCategory, 'labelCategoryId' | 'name'>;

interface Props {
  funnelSteps: NexoyaFunnelStepV2[];
  hasBidStrategy: boolean;
  hasBudgetLimits: boolean;
  hasTargetLimits: boolean;
  isAttributed: boolean;
  hasLabels: boolean;
  tableView: 'simple' | 'advanced';
  handleSelectAllContents: () => void;
  allContentsSelected: boolean;
  hasSelfServicePortfolioFeatureEnabled?: boolean;
  /**
   * The team's Content Labels v2 categories, in server order — one column each,
   * immediately after Content (fig4). Empty when the flag is off, when the read
   * is still in flight, or when the team has none, and the column list is then
   * byte-identical to the one this tab has always rendered.
   */
  contentLabelCategories?: ContentLabelColumnCategory[];
}

export const getColumns = ({
  funnelSteps,
  hasLabels,
  hasBidStrategy,
  hasBudgetLimits,
  hasTargetLimits,
  isAttributed,
  tableView,
  handleSelectAllContents,
  allContentsSelected,
  hasSelfServicePortfolioFeatureEnabled,
  contentLabelCategories = [],
}: Props) => {
  const expanderColumn = {
    Header: '',
    accessor: 'expander',
    id: 'expander',
    width: '50',
    disableSortBy: true,
    disableHiding: true,
    disableSticky: true,
    isHiddenInManager: true,
    Cell: ({ row }) => {
      return row.original.hasChildren ? (
        <Styles.ChevronWrap expanded={row.isExpanded} className="!py-2.5" {...row.getToggleRowExpandedProps()}>
          <SvgChevronDown />
        </Styles.ChevronWrap>
      ) : null;
    },
  };

  /**
   * One column per Content Labels v2 category (fig4: `COUNTRY LABEL`,
   * `PRODUCT FAMILY LABEL`), sitting immediately right of Content.
   *
   * - `accessor` is a FUNCTION, never the string id: react-table resolves a
   *   string accessor as a lodash path, and a category uuid carrying a `.`
   *   would read `undefined` off a row that does carry the cell — a blank
   *   column with a green build.
   * - `disableSortBy`, because `NexoyaPortfolioParentContentsSortField` has no
   *   content-label member and the table holds ONE server page of 246: a
   *   client-side sort would sort 50 of 12,266 rows and call it sorted.
   * - No `isHiddenInManager`, so "Manage columns" lists it; no `disableSticky`,
   *   so it can be pinned next to the frozen Content column.
   * - `minWidth: 200` plus the shared resize handle, persisted per user and
   *   portfolio. Figma's 200/328 widths track the category name; letting the
   *   existing sizing handle it is what FIGMA-SPEC asks for.
   */
  const CONTENT_LABEL_COLUMNS = contentLabelCategories.map((category) => {
    const columnId = contentLabelColumnId(category.labelCategoryId);
    const noun = labelColumnLabel(category);

    return {
      Header: <HeaderCell>{noun}</HeaderCell>,
      tableManagerHeader: <BigHeaderCell>{noun}</BigHeaderCell>,
      id: columnId,
      accessor: (row: Record<string, unknown>) => row?.[columnId],
      className: 'border-right',
      minWidth: 200,
      enableColumnResize: true,
      disableSortBy: true,
    };
  });

  const SIMPLE_COLUMNS = [
    {
      Header: '',
      accessor: 'editRow',
      id: 'editRow',
      width: '48',
      disableSortBy: true,
      disableHiding: true,
      isHiddenInManager: true,
      disableSticky: true,
    },
    {
      Header: <Checkbox className="!pl-0 !pr-0.5" onClick={handleSelectAllContents} checked={allContentsSelected} />,
      id: 'selectContent',
      accessor: 'selectContent',
      disableSortBy: true,
      enableColumnResize: false,
      width: 45,
      isHiddenInManager: true,
      disableSticky: true,
    },

    {
      Header: (
        <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.ContentTitle}>Content</HeaderSortCell>
      ),
      tableManagerHeader: <BigHeaderCell>Content</BigHeaderCell>,
      id: 'content',
      accessor: 'content',
      className: 'border-right',
      minWidth: 600,
      disableHiding: true,
      disableSortBy: true,
      enableColumnResize: true,
    },
    ...CONTENT_LABEL_COLUMNS,
    {
      Header: (
        <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.IsIncludedInOptimization}>
          Optimization
        </HeaderSortCell>
      ),
      tableManagerHeader: <BigHeaderCell>Optimization</BigHeaderCell>,
      width: 200,
      accessor: 'optimization',
      className: 'border-right',
      enableColumnResize: true,
      disableSortBy: true,
      disableSticky: true,
    },
    {
      Header: (
        <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.ContentType}>Content level</HeaderSortCell>
      ),
      tableManagerHeader: <BigHeaderCell>Content level</BigHeaderCell>,
      width: 200,
      accessor: 'contentLevel',
      className: 'border-right',
      enableColumnResize: true,
      disableSortBy: true,
      disableSticky: true,
    },
    {
      Header: (
        <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.IsRuleManaged}>
          Content mode
        </HeaderSortCell>
      ),
      tableManagerHeader: <BigHeaderCell>Content mode</BigHeaderCell>,
      width: 200,
      accessor: 'contentMode',
      className: 'border-right',
      enableColumnResize: true,
      disableSortBy: true,
      disableSticky: true,
    },
    /**
     * The LEGACY per-portfolio label column (`portfolioMeta.labels`), which is
     * a different mechanism from Content Labels v2 entirely. A team with two
     * categories would otherwise show three columns all captioned around the
     * word "label"; fig4 draws none of the old one, so the new columns
     * supersede it. A portfolio with no categories keeps it untouched, which is
     * what the flag-off path rests on.
     */
    hasLabels && CONTENT_LABEL_COLUMNS.length === 0
      ? {
          Header: (
            <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.LabelName}>Label</HeaderSortCell>
          ),
          tableManagerHeader: <BigHeaderCell>Label</BigHeaderCell>,
          enableColumnResize: true,
          className: 'border-right',
          accessor: 'label',
          minWidth: 200,
          disableSortBy: true,
          disableSticky: true,
        }
      : null,
    !isAttributed
      ? {
          Header: (
            <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.ImpactGroupName}>
              Impact group
            </HeaderSortCell>
          ),
          tableManagerHeader: <BigHeaderCell>Impact group</BigHeaderCell>,
          accessor: 'impactGroup',
          enableColumnResize: true,
          className: 'border-right',
          minWidth: 250,
          disableSortBy: true,
          disableSticky: true,
        }
      : null,
    hasBudgetLimits
      ? {
          Header: <HeaderCell>Budget limit</HeaderCell>,
          tableManagerHeader: <BigHeaderCell>Budget limit</BigHeaderCell>,
          enableColumnResize: true,
          className: 'border-right',
          disableSortBy: true,
          accessor: 'budgetLimit',
          minWidth: 250,
          disableSticky: true,
        }
      : null,
    hasTargetLimits
      ? {
          Header: <HeaderCell>Target limit</HeaderCell>,
          tableManagerHeader: <BigHeaderCell>Target limit</BigHeaderCell>,
          enableColumnResize: true,
          className: 'border-right',
          disableSortBy: true,
          accessor: 'targetLimit',
          minWidth: 250,
          disableSticky: true,
        }
      : null,
    hasSelfServicePortfolioFeatureEnabled
      ? {
          Header: (
            <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.ContentRuleName}>
              Content rule
            </HeaderSortCell>
          ),
          tableManagerHeader: <BigHeaderCell>Content rule</BigHeaderCell>,
          accessor: 'contentRule',
          enableColumnResize: true,
          className: 'border-right',
          minWidth: 250,
          disableSortBy: true,
          disableSticky: true,
        }
      : null,
    !isAttributed && hasSelfServicePortfolioFeatureEnabled
      ? {
          Header: (
            <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.ImpactGroupRuleName}>
              Impact group rule
            </HeaderSortCell>
          ),
          tableManagerHeader: <BigHeaderCell>Impact group rule</BigHeaderCell>,
          accessor: 'impactGroupRule',
          enableColumnResize: true,
          className: 'border-right',
          minWidth: 250,
          disableSortBy: true,
          disableSticky: true,
        }
      : null,
    isAttributed
      ? {
          Header: (
            <HeaderSortCell sortFieldKey={NexoyaPortfolioParentContentsSortField.AttributionRuleName}>
              Attribution rule
            </HeaderSortCell>
          ),
          tableManagerHeader: <BigHeaderCell>Attribution rule</BigHeaderCell>,
          accessor: 'attributionRule',
          enableColumnResize: true,
          className: 'border-right',
          minWidth: 250,
          disableSortBy: true,
          disableSticky: true,
        }
      : null,
  ].filter(Boolean);

  const ADVANCED_COLUMNS = [
    hasBidStrategy
      ? {
          Header: <HeaderCell>Bidding strategy</HeaderCell>,
          tableManagerHeader: <BigHeaderCell>Bidding strategy</BigHeaderCell>,
          enableColumnResize: true,
          accessor: 'bidStrategy',
          className: 'border-right',
          disableSortBy: true,
          minWidth: 250,
          disableSticky: true,
        }
      : null,
    ...funnelSteps.map((funnelStep) => ({
      className: 'border-right',
      Header: <HeaderCell>{funnelStep.title}</HeaderCell>,
      tableManagerHeader: <BigHeaderCell>{funnelStep.title}</BigHeaderCell>,
      accessor: `funnel_${funnelStep.funnelStepId}`,
      disableSortBy: true,
      enableColumnResize: true,
      minWidth: 250,
      disableSticky: true,
    })),
  ].filter(Boolean);

  if (tableView === 'simple') {
    return SIMPLE_COLUMNS;
  }

  if (tableView === 'advanced') {
    const baseColumns = SIMPLE_COLUMNS;

    baseColumns.splice(2, 0, expanderColumn);

    return [...baseColumns, ...ADVANCED_COLUMNS];
  }
};
